/**
 * The task edit form on the Tasks page (1.0.0-rc.14, #12), as plain functions. The form remembers the version of the
 * task it started from (`base`): when the task changes meanwhile (a status change, someone else's edit, a refresh) the
 * fields the person has not touched follow it, and the ones they changed stay as they typed them. A save sends only the
 * changed fields with the base's updatedAt, and the API refuses it with 409 TASK_CHANGED when the task changed since.
 * Before, any change of updatedAt (a note too) reset the form and lost what was being typed, and a save sent every
 * field, putting back what someone else had just changed.
 */

/** The editable fields, as the form holds them (assignee as AssigneeSelect's value, due as a datetime-local value). */
export interface TaskForm {
  title: string;
  details: string;
  assignee: string;
  due: string;
}
export type TaskField = keyof TaskForm;
export const TASK_FIELDS: readonly TaskField[] = ['title', 'details', 'assignee', 'due'];

/** What each field is called in a message. */
export const TASK_FIELD_LABELS: Record<TaskField, string> = { title: 'the task', details: 'the details', assignee: 'who it is assigned to', due: 'the deadline' };

/** The fields the person changed since `base`. */
export function changedFields(base: TaskForm, edit: TaskForm): TaskField[] {
  return TASK_FIELDS.filter((f) => edit[f] !== base[f]);
}

/**
 * The form after the task changed from `base` to `next`: untouched fields take `next`'s values, changed ones are kept.
 * `conflicts`: fields the person changed that were also changed to something else on the server.
 */
export function rebaseTaskForm(base: TaskForm, edit: TaskForm, next: TaskForm): { edit: TaskForm; conflicts: TaskField[] } {
  const out = { ...next };
  const conflicts: TaskField[] = [];
  for (const f of TASK_FIELDS) {
    if (edit[f] === base[f]) continue;
    out[f] = edit[f];
    if (next[f] !== base[f] && next[f] !== edit[f]) conflicts.push(f);
  }
  return { edit: out, conflicts };
}

/** "the task and the deadline" */
export function fieldList(fields: readonly TaskField[]): string {
  const names = fields.map((f) => TASK_FIELD_LABELS[f]);
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}
